import { useState } from 'react'
import type { ListSummary } from '../lib/api.ts'
import { api } from '../lib/api.ts'
import { Button } from './ui/button.tsx'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from './ui/dialog.tsx'
import { Input, Label } from './ui/input.tsx'
import { Alert } from './ui/alert.tsx'
import { useToast } from './ui/toast.tsx'
import { ConfirmDialog } from './ConfirmDialog.tsx'

export function ListSettingsDialog({
  list, open, onOpenChange, onSaved,
}: { list: ListSummary; open: boolean; onOpenChange: (v: boolean) => void; onSaved: () => void }) {
  const [name, setName] = useState(list.name)
  const [studentName, setStudentName] = useState(list.studentName ?? '')
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [removingPassword, setRemovingPassword] = useState(false)
  const toast = useToast()

  async function save(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api.updateList(list.id, { name, studentName: studentName.trim() || null })
      if (next) await api.setPassword(list.id, next, current || undefined)
      onOpenChange(false)
      onSaved()
      toast(next ? 'Nastavení uloženo, heslo změněno.' : 'Nastavení uloženo.')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Uložení se nepodařilo')
    } finally {
      setBusy(false)
    }
  }

  async function removePassword() {
    setBusy(true)
    setError(null)
    try {
      await api.setPassword(list.id, null, current || undefined)
      onOpenChange(false)
      onSaved()
      toast('Heslo odebráno.')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Odebrání hesla se nepodařilo')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>Nastavení seznamu</DialogTitle>
        <DialogDescription>Název, jméno studenta a ochrana heslem.</DialogDescription>

        <form onSubmit={save} className="mt-4 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="list-name">Název seznamu</Label>
            <Input id="list-name" value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="student-name">Jméno studenta</Label>
            <Input id="student-name" value={studentName} onChange={(e) => setStudentName(e.target.value)} placeholder="Objeví se v exportu a na tiskové stránce" maxLength={120} />
          </div>

          <div className="space-y-2 rounded-[var(--radius-lg)] border border-[var(--border)] p-3">
            <p className="text-[length:var(--text-sm)] font-medium">
              {list.hasPassword ? 'Seznam je chráněný heslem' : 'Seznam není chráněný'}
            </p>
            <p className="text-[length:var(--text-xs)] text-[var(--muted-foreground)]">
              Heslo chrání jen úpravy. Prohlížení, tisk a export zůstávají komukoli otevřené.
            </p>

            {list.hasPassword && (
              <div className="space-y-1.5">
                <Label htmlFor="current-password">Současné heslo</Label>
                <Input id="current-password" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="new-password">{list.hasPassword ? 'Nové heslo' : 'Nastavit heslo'}</Label>
              <Input id="new-password" type="password" value={next} onChange={(e) => setNext(e.target.value)} placeholder={list.hasPassword ? 'Nechat prázdné = ponechat' : 'Nepovinné'} />
            </div>

            {list.hasPassword && (
              <Button type="button" variant="ghost" size="sm" className="text-[var(--destructive)]" onClick={() => setRemovingPassword(true)} disabled={busy}>
                Odebrat heslo
              </Button>
            )}
          </div>

          {error && <Alert variant="destructive">{error}</Alert>}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Zrušit</Button>
            <Button type="submit" disabled={busy}>{busy ? 'Ukládám…' : 'Uložit'}</Button>
          </div>
        </form>
      </DialogContent>

      <ConfirmDialog
        open={removingPassword}
        onOpenChange={setRemovingPassword}
        title="Odebrat heslo ze seznamu?"
        description={
          <>
            Seznam pak bude moct <strong>upravovat kdokoli</strong>, kdo zná odkaz — přidávat
            i odebírat díla. Čtení, tisk a export jsou otevřené tak jako tak.
            Heslo jde kdykoli nastavit znovu.
          </>
        }
        confirmLabel="Odebrat heslo"
        destructive
        onConfirm={async () => {
          await removePassword()
          setRemovingPassword(false)
        }}
      />
    </Dialog>
  )
}
